import { useRef, useState } from "react";
import type { AudioClipInstance, TrackDefinition } from "@/api/client";

// ─── Types ────────────────────────────────────────────────────────────────────

export type MixSnapshot = {
  clips: AudioClipInstance[];
  narrationVolume: number;
  ambientVolume: number;
  sfxVolume: number;
};

export type AudioSelectionData = {
  voices?: unknown[];
  auto_backgrounds?: boolean;
  tts_voice_id?: string | null;
  tts_provider?: string | null;
  narration_volume?: number;
  ambient_volume?: number;
  sfx_volume?: number;
  clips?: AudioClipInstance[];
  tracks?: TrackDefinition[];
};

/** Signature of commitClips — kept as a ref to break the mutual dependency
 *  between this hook and useClipMutations. */
export type CommitClipsFn = (
  next: AudioClipInstance[],
  opts?: { remix?: boolean; vols?: { narration?: number; ambient?: number; sfx?: number } },
  nextTracks?: TrackDefinition[],
) => void;

const MAX_HISTORY = 50;

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Multi-level undo/redo for the audio mix state.
 *
 * Uses a `commitClipsFnRef` (set by the view after both hooks are initialised)
 * instead of taking `commitClips` directly, which would create a circular
 * dependency with `useClipMutations`.
 */
export function useClipHistory(
  clipsRef: React.MutableRefObject<AudioClipInstance[]>,
  narrationVolumeRef: React.MutableRefObject<number>,
  ambientVolumeRef:   React.MutableRefObject<number>,
  sfxVolumeRef:       React.MutableRefObject<number>,
  setNarrationVolume: (v: number) => void,
  setAmbientVolume:   (v: number) => void,
  setSfxVolume:       (v: number) => void,
  setSelectedClipId:  (id: string | null) => void,
  /** Ref that the view populates with `commitClips` after mutations hook is ready. */
  commitClipsFnRef: React.MutableRefObject<CommitClipsFn>,
) {
  const pastRef   = useRef<MixSnapshot[]>([]);
  const futureRef = useRef<MixSnapshot[]>([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  // ── Snapshot helpers ────────────────────────────────────────────────────────

  const currentSnapshot = (): MixSnapshot => ({
    clips:            clipsRef.current.map((c) => ({ ...c })),
    narrationVolume:  narrationVolumeRef.current,
    ambientVolume:    ambientVolumeRef.current,
    sfxVolume:        sfxVolumeRef.current,
  });

  const pushHistory = (snap: MixSnapshot) => {
    pastRef.current.push(snap);
    if (pastRef.current.length > MAX_HISTORY) pastRef.current.shift();
    futureRef.current = [];
    setCanUndo(true);
    setCanRedo(false);
  };

  // ── Public API ──────────────────────────────────────────────────────────────

  /** Immediately push the current state as an undoable entry. */
  const captureSnapshot = () => pushHistory(currentSnapshot());

  // Deferred capture — stash now, commit only if the gesture actually moved.
  const pendingSnapshotRef = useRef<MixSnapshot | null>(null);
  const capturePending  = () => { pendingSnapshotRef.current = currentSnapshot(); };
  const flushPending    = () => {
    if (!pendingSnapshotRef.current) return;
    pushHistory(pendingSnapshotRef.current);
    pendingSnapshotRef.current = null;
  };
  const discardPending  = () => { pendingSnapshotRef.current = null; };

  const applySnapshot = (s: MixSnapshot) => {
    narrationVolumeRef.current = s.narrationVolume;
    ambientVolumeRef.current   = s.ambientVolume;
    sfxVolumeRef.current       = s.sfxVolume;
    setNarrationVolume(s.narrationVolume);
    setAmbientVolume(s.ambientVolume);
    setSfxVolume(s.sfxVolume);
    setSelectedClipId(null);
    commitClipsFnRef.current(s.clips, {
      remix: true,
      vols: { narration: s.narrationVolume, ambient: s.ambientVolume, sfx: s.sfxVolume },
    });
  };

  const undo = () => {
    if (pastRef.current.length === 0) return;
    const previous = pastRef.current.pop()!;
    futureRef.current.push(currentSnapshot());
    setCanUndo(pastRef.current.length > 0);
    setCanRedo(true);
    applySnapshot(previous);
  };

  const redo = () => {
    if (futureRef.current.length === 0) return;
    const next = futureRef.current.pop()!;
    pastRef.current.push(currentSnapshot());
    if (pastRef.current.length > MAX_HISTORY) pastRef.current.shift();
    setCanUndo(true);
    setCanRedo(futureRef.current.length > 0);
    applySnapshot(next);
  };

  return {
    canUndo, canRedo,
    captureSnapshot, capturePending, flushPending, discardPending,
    undo, redo,
  };
}
